import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { Box, Typography, MenuItem, Select as MuiSelect } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { capitalizeFirstLetter } from "../../../hooks/use-capitalize-first-letter";
import { formatPhoneNumber } from "../../../hooks/use-format-phone-number";
import { UpdatePendingPatient, ResendPendingPatientInvite, CancelPendingPatient, GetPendingPatients } from "../../../apollo-client/api-query";
import { buildUpdatePendingPatientInput } from "../../../utils/patient-transforms";
import { computePermissions } from "../../../utils/permission";
import { ViewPermissions } from "../../../constants/permissions";
import BaseButton from "../../BaseButton";
import BaseInput from "../../BaseInput";

const statusStyles = {
  PENDING: { bg: '#FFF3CD', color: '#856404', label: 'Unpaid' },
  EXPIRED: { bg: '#F8D7DA', color: '#721C23', label: 'Expired' },
  CANCELLED: { bg: '#E2E3E5', color: '#383D41', label: 'Cancelled' },
  FAILED: { bg: '#F8D7DA', color: '#721C23', label: 'Failed' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const LabelRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: '6px', borderBottom: '1px solid #F0F0F0' }}>
    <Typography variant="body" color="primary.darkGray" sx={{ width: '140px', flexShrink: 0, fontSize: '13px' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const inputSx = {
  '& .MuiOutlinedInput-root': { boxShadow: '0px 2px 10px 0px #5B87EF33' },
};

const statusMessages = {
  EXPIRED: 'This invitation has expired and can no longer be managed.',
  CANCELLED: 'This invitation was cancelled.',
  FAILED: 'This invitation failed to process.',
};

const getUpdateErrorMessage = (error) => {
  const gqlError = error?.graphQLErrors?.[0];
  const type = gqlError?.extensions?.type;
  if (type === 'DUPLICATE_INFO') return 'A pending patient with this email already exists.';
  return gqlError?.message || 'An error occurred while updating. Please try again.';
};

function InactivePatientModal({ close, data }) {
  const style = statusStyles[data?.status] || statusStyles.FAILED;
  const message = statusMessages[data?.status] || 'This invitation is no longer active.';

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: '570px' },
        borderRadius: '16px',
        backgroundColor: 'background.white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 16px 8px',
          borderBottom: '1px solid',
          borderColor: 'primary.mediumGray',
        }}
      >
        <Typography variant="h1">Pending Patient</Typography>
        <CloseIcon
          sx={{ width: '32px', height: '32px', color: 'primary.black', cursor: 'pointer' }}
          onClick={close}
        />
      </Box>

      <Box sx={{ padding: '16px' }}>
        {/* Status badge + message */}
        <Box sx={{ mb: '16px' }}>
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              backgroundColor: style.bg,
              color: style.color,
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {style.label}
          </Box>
          <Typography variant="body" color="primary.darkGray" sx={{ fontSize: '13px', mt: '8px' }}>
            {message}
          </Typography>
        </Box>

        {/* Read-only details */}
        <Box>
          <LabelRow label="Name">
            <Typography variant="body" sx={{ fontSize: '13px', fontWeight: 500 }}>{data?.name || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Email">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.email || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Kit Code">
            <Typography variant="body" sx={{ fontSize: '13px', fontFamily: 'monospace' }}>{data?.kitCode || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Date of Birth">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.dateOfBirth || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Invited by">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.physicianName || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Invited on">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{formatDate(data?.createdAt)}</Typography>
          </LabelRow>
        </Box>
      </Box>

      <Box
        sx={{
          padding: '12px 16px',
          borderTop: '1px solid',
          borderColor: 'primary.mediumGray',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <BaseButton
          onClick={close}
          sx={{
            width: { xs: '100%', sm: '140px' },
            background: '#46545F',
            '&:hover': { background: '#2A3342' },
          }}
        >
          Close
        </BaseButton>
      </Box>
    </Box>
  );
}

export default function PendingPatientDetailModal({ close, data: initialData }) {
  // Local patient data — starts from prop, updated from mutation responses
  const [patientData, setPatientData] = useState(initialData);
  const data = patientData;

  const [confirmAction, setConfirmAction] = useState(null); // 'resend' | 'cancel' | null
  const [isEditing, setIsEditing] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Edit form state
  const [firstName, setFirstName] = useState(data?.firstName || '');
  const [lastName, setLastName] = useState(data?.lastName || '');
  const [email, setEmail] = useState(data?.email || '');
  const [phone, setPhone] = useState(data?.phone || '');
  const [dateOfBirth, setDateOfBirth] = useState(data?.dateOfBirth || '');
  const [sex, setSex] = useState(data?.sex || '');
  const [physicianNotes, setPhysicianNotes] = useState(data?.physicianNotes || '');

  const auth = useSelector(state => state.auth);

  // Apply mutation response to local state so the modal reflects changes immediately
  const applyUpdateResponse = (responseNode) => {
    if (!responseNode) return;
    const updated = {
      ...data,
      name: responseNode.name ?? data?.name,
      firstName: responseNode.firstName ?? data?.firstName,
      lastName: responseNode.lastName ?? data?.lastName,
      email: responseNode.email ?? data?.email,
      phone: responseNode.phoneNumber ?? data?.phone,
      dateOfBirth: responseNode.dateOfBirth ?? data?.dateOfBirth,
      sex: responseNode.sex ?? data?.sex,
      physicianNotes: responseNode.physicianNotes ?? data?.physicianNotes,
      emailLastSentAt: responseNode.emailLastSentAt ?? data?.emailLastSentAt,
      reminderCount: responseNode.reminderCount ?? data?.reminderCount,
      updatedAt: responseNode.updatedAt ?? data?.updatedAt,
    };
    setPatientData(updated);
    // Sync form fields with new values
    setFirstName(updated.firstName || '');
    setLastName(updated.lastName || '');
    setEmail(updated.email || '');
    setPhone(updated.phone || '');
    setDateOfBirth(updated.dateOfBirth || '');
    setSex(updated.sex || '');
    setPhysicianNotes(updated.physicianNotes || '');
  };

  // Role-based permissions for this user. Pending-patient mutations are
  // separately gated: edit is more restricted (Admin/StaffPhys/Manager) than
  // resend/cancel (those also allow Support).
  // In addition, the physician who created an invitation can always manage it
  // (matches backend `member_self: true` on these permissions).
  const myPermissions = useMemo(
    () => computePermissions({ roles: auth?.roles }),
    [auth?.roles]
  );
  const isPending = data?.status === 'PENDING';
  const isInvitationOwner = !!data?.physicianId && data?.physicianId === auth?.id;

  const canEdit = isPending && (
    myPermissions.includes(ViewPermissions.EDIT_PENDING_PATIENT) || isInvitationOwner
  );
  const canResend = isPending && (
    myPermissions.includes(ViewPermissions.RESEND_PENDING_PATIENT_INVITE) || isInvitationOwner
  );
  const canCancel = isPending && (
    myPermissions.includes(ViewPermissions.CANCEL_PENDING_PATIENT) || isInvitationOwner
  );
  const canReadNotes = (
    myPermissions.includes(ViewPermissions.READ_PENDING_PATIENT_NOTES) || isInvitationOwner
  );
  const hasAnyAction = canEdit || canResend || canCancel;

  const refetchPending = [{ query: GetPendingPatients, variables: { first: 100 } }];

  const [updatePendingPatient, { loading: updating }] = useMutation(UpdatePendingPatient, {
    refetchQueries: refetchPending,
    // Suppress the global error toast — errors are shown inline in the edit form instead.
    context: { suppressToast: true },
    onCompleted: (result) => {
      setUpdateError(null);
      applyUpdateResponse(result?.updatePendingPatient);
      setIsEditing(false);
    },
    onError: (error) => {
      setUpdateError(getUpdateErrorMessage(error));
    },
  });

  const [resendInvite, { loading: resending }] = useMutation(ResendPendingPatientInvite, {
    refetchQueries: refetchPending,
    onCompleted: (result) => {
      applyUpdateResponse(result?.resendPendingPatientInvite);
      setConfirmAction(null);
    },
    onError: () => {
      setConfirmAction(null);
    },
  });

  const [cancelInvite, { loading: cancelling }] = useMutation(CancelPendingPatient, {
    refetchQueries: refetchPending,
    onCompleted: () => {
      close();
    },
    onError: () => {
      setConfirmAction(null);
    },
  });

  const hasChanges = useMemo(() => {
    return firstName !== (data?.firstName || '') ||
           lastName !== (data?.lastName || '') ||
           email !== (data?.email || '') ||
           phone !== (data?.phone || '') ||
           dateOfBirth !== (data?.dateOfBirth || '') ||
           sex !== (data?.sex || '') ||
           physicianNotes !== (data?.physicianNotes || '');
  }, [firstName, lastName, email, phone, dateOfBirth, sex, physicianNotes, data]);

  const emailChanged = email !== (data?.email || '');

  // Clear update error when user edits any field
  useEffect(() => {
    if (updateError) setUpdateError(null);
  }, [firstName, lastName, email, phone, dateOfBirth, sex, physicianNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-PENDING statuses get a simpler informational modal
  if (data?.status && data.status !== 'PENDING') {
    return <InactivePatientModal close={close} data={data} />;
  }

  const handleUpdate = () => {
    const input = buildUpdatePendingPatientInput({
      id: data?.id,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      email: email || undefined,
      phone: phone || undefined,
      dateOfBirth: dateOfBirth || undefined,
      sex: sex || undefined,
      physicianNotes: physicianNotes || undefined,
    });
    updatePendingPatient({ variables: { input } });
  };

  const handleCancelEdit = () => {
    setFirstName(data?.firstName || '');
    setLastName(data?.lastName || '');
    setEmail(data?.email || '');
    setPhone(data?.phone || '');
    setDateOfBirth(data?.dateOfBirth || '');
    setSex(data?.sex || '');
    setPhysicianNotes(data?.physicianNotes || '');
    setIsEditing(false);
  };

  const style = statusStyles[data?.status] || statusStyles.PENDING;

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: '570px' },
        maxHeight: '90vh',
        borderRadius: '16px',
        backgroundColor: 'background.white',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 16px 8px',
          borderBottom: '1px solid',
          borderColor: 'primary.mediumGray',
        }}
      >
        <Typography variant="h1">Pending Patient Details</Typography>
        <CloseIcon
          sx={{ width: '32px', height: '32px', color: 'primary.black', cursor: 'pointer' }}
          onClick={close}
        />
      </Box>

      {/* Content */}
      <Box sx={{ padding: '16px' }}>
        {/* Status badge + Edit button */}
        <Box sx={{ mb: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              backgroundColor: style.bg,
              color: style.color,
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {style.label}
          </Box>
          {canEdit && !isEditing && (
            <BaseButton
              buttonType="default"
              width="auto"
              height="32px"
              sx={{ px: 2, fontSize: '12px' }}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </BaseButton>
          )}
        </Box>

        {/* Demographics */}
        <Typography variant="h3" sx={{ mb: '8px' }}>Demographics</Typography>
        <Box sx={{ mb: '20px' }}>
          {isEditing ? (
            <>
              <LabelRow label="First Name">
                <BaseInput
                  fullWidth
                  height={32}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                  sx={inputSx}
                />
              </LabelRow>
              <LabelRow label="Last Name">
                <BaseInput
                  fullWidth
                  height={32}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                  sx={inputSx}
                />
              </LabelRow>
            </>
          ) : (
            <LabelRow label="Name">
              <Typography variant="body" sx={{ fontSize: '13px', fontWeight: 500 }}>{data?.name || '-'}</Typography>
            </LabelRow>
          )}
          <LabelRow label="Email">
            {isEditing ? (
              <Box>
                <BaseInput
                  fullWidth
                  height={32}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                  sx={inputSx}
                />
                {emailChanged && (
                  <Typography variant="body" color="primary.mainBlue" sx={{ fontSize: '11px', mt: '2px' }}>
                    A new invitation email will be sent to this address.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.email || '-'}</Typography>
            )}
          </LabelRow>
          <LabelRow label="Phone">
            {isEditing ? (
              <BaseInput
                fullWidth
                height={32}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                sx={inputSx}
              />
            ) : (
              <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.phone ? formatPhoneNumber(data.phone) : '-'}</Typography>
            )}
          </LabelRow>
          <LabelRow label="Date of Birth">
            {isEditing ? (
              <BaseInput
                fullWidth
                height={32}
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                sx={inputSx}
              />
            ) : (
              <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.dateOfBirth || '-'}</Typography>
            )}
          </LabelRow>
          <LabelRow label="Sex">
            {isEditing ? (
              <MuiSelect
                value={sex}
                onChange={(e) => setSex(e.target.value)}
                displayEmpty
                size="small"
                sx={{
                  fontSize: '13px',
                  height: '32px',
                  minWidth: '140px',
                  borderRadius: '6px',
                  boxShadow: '0px 2px 10px 0px #5B87EF33',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ABB7C0' },
                }}
              >
                <MenuItem value="" disabled><em>Select</em></MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </MuiSelect>
            ) : data?.sex ? (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: 'primary.lightGreen1',
                  padding: '2px 8px',
                  borderRadius: '4px',
                }}
              >
                <Typography variant="h4" color="primary.black" fontSize="13px">
                  {capitalizeFirstLetter(data.sex)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body" sx={{ fontSize: '13px' }}>-</Typography>
            )}
          </LabelRow>
          <LabelRow label="Kit Code">
            <Typography variant="body" sx={{ fontSize: '13px', fontFamily: 'monospace' }}>{data?.kitCode || '-'}</Typography>
          </LabelRow>
        </Box>

        {/* Invitation Details */}
        <Typography variant="h3" sx={{ mb: '8px' }}>Invitation Details</Typography>
        <Box sx={{ mb: '20px' }}>
          <LabelRow label="Invited by">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.physicianName || '-'}</Typography>
          </LabelRow>
          <LabelRow label="Invited on">
            <Typography variant="body" sx={{ fontSize: '13px' }}>{formatDate(data?.createdAt)}</Typography>
          </LabelRow>
          {data?.emailLastSentAt && (
            <LabelRow label="Last email sent">
              <Typography variant="body" sx={{ fontSize: '13px' }}>
                {formatDate(data?.emailLastSentAt)}
                {data?.reminderCount > 0 && (
                  <Typography component="span" variant="body" color="primary.darkGray" sx={{ fontSize: '11px', ml: '6px' }}>
                    ({data.reminderCount} reminder{data.reminderCount !== 1 ? 's' : ''} sent)
                  </Typography>
                )}
              </Typography>
            </LabelRow>
          )}
          {canReadNotes && (
            <LabelRow label="Physician Notes">
              {isEditing ? (
                <BaseInput
                  fullWidth
                  height={32}
                  value={physicianNotes}
                  onChange={(e) => setPhysicianNotes(e.target.value)}
                  placeholder="Notes"
                  inputStyles={{ fontSize: '13px', border: '#ABB7C0', borderRadius: '6px', background: '#fff' }}
                  sx={inputSx}
                />
              ) : (
                <Typography variant="body" sx={{ fontSize: '13px' }}>{data?.physicianNotes || '-'}</Typography>
              )}
            </LabelRow>
          )}
        </Box>

        {/* Action confirmations */}
        {confirmAction === 'resend' && (
          <Box sx={{ p: 2, mb: 2, borderRadius: '8px', bgcolor: '#F5F5F5' }}>
            <Typography variant="body" sx={{ fontSize: '13px', mb: 2 }}>
              Resend the invitation email to <strong>{data?.email}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <BaseButton
                type="button"
                onClick={() => setConfirmAction(null)}
                sx={{
                  width: { xs: '100%', sm: '140px' },
                  background: '#46545F',
                  '&:hover': { background: '#2A3342' },
                }}
              >
                No
              </BaseButton>
              <BaseButton
                buttonType="primary"
                disabled={resending}
                loading={resending}
                onClick={() => resendInvite({ variables: { pendingPatientId: data?.id } })}
                sx={{
                  width: { xs: '100%', sm: '140px' },
                }}
              >
                Yes
              </BaseButton>
            </Box>
          </Box>
        )}
        {updateError && (
          <Box sx={{ p: 2, mb: 2, borderRadius: '8px', bgcolor: '#FFF3F3', border: '1px solid #F5C6CB' }}>
            <Typography variant="body" sx={{ fontSize: '13px', color: '#721C23' }}>
              {updateError}
            </Typography>
          </Box>
        )}
        {confirmAction === 'cancel' && (
          <Box sx={{ p: 2, mb: 2, borderRadius: '8px', bgcolor: '#FFF3F3' }}>
            <Typography variant="body" sx={{ fontSize: '13px', mb: 2 }}>
              Cancel the invitation for <strong>{data?.name}</strong>? This cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <BaseButton
                type="button"
                onClick={() => setConfirmAction(null)}
                sx={{
                  width: { xs: '100%', sm: '140px' },
                  background: '#46545F',
                  '&:hover': { background: '#2A3342' },
                }}
              >
                No
              </BaseButton>
              <BaseButton
                buttonType="primary"
                disabled={cancelling}
                loading={cancelling}
                onClick={() => cancelInvite({ variables: { pendingPatientId: data?.id } })}
                sx={{
                  width: { xs: '100%', sm: '140px' },
                  background: '#DC3545',
                  '&:hover': { background: '#B02A37' },
                }}
              >
                Yes
              </BaseButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          padding: '12px 16px',
          borderTop: '1px solid',
          borderColor: 'primary.mediumGray',
        }}
      >
        {isEditing ? (
          <>
            <BaseButton
              type="button"
              onClick={handleCancelEdit}
              disabled={updating}
              sx={{
                width: { xs: '100%', sm: '140px' },
                background: '#46545F',
                '&:hover': { background: '#2A3342' },
              }}
            >
              Cancel
            </BaseButton>
            <BaseButton
              onClick={handleUpdate}
              disabled={!hasChanges || updating}
              loading={updating}
              buttonType="primary"
              sx={{
                width: { xs: '100%', sm: '140px' },
                opacity: hasChanges ? 1 : 0.5,
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                '&:disabled': {
                  background: '#ABB7C0',
                  color: 'white',
                  cursor: 'not-allowed',
                },
              }}
            >
              Update
            </BaseButton>
          </>
        ) : (
          <>
            <BaseButton
              type="button"
              onClick={close}
              sx={{
                width: { xs: '100%', sm: '140px' },
                background: '#46545F',
                '&:hover': { background: '#2A3342' },
              }}
            >
              Close
            </BaseButton>
            {!confirmAction && hasAnyAction && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {canResend && (
                  <BaseButton
                    type="button"
                    onClick={() => setConfirmAction('resend')}
                    buttonType="primary"
                    sx={{ width: { xs: '100%', sm: 'auto' }, px: 3 }}
                  >
                    Resend Invitation
                  </BaseButton>
                )}
                {canCancel && (
                  <BaseButton
                    type="button"
                    onClick={() => setConfirmAction('cancel')}
                    buttonType="primary"
                    sx={{ width: { xs: '100%', sm: 'auto' }, px: 3 }}
                  >
                    Cancel Invitation
                  </BaseButton>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
